import { jwtDecode } from 'jwt-decode';

import type { DecodedJwt } from '@/types/auth';

/**
 * Returns true when the JWT is expired, missing, or unparseable.
 *
 * Single source of truth for client-side expiry checks. Used by:
 *  - axios.ts request interceptor (pre-flight skip + redirect)
 *  - AuthContext.tsx initial-mount rehydration (pre-check stored token)
 *
 * @param token  Raw JWT string (or null when storage is empty).
 * @param skewSeconds  Safety skew so a token about to expire is treated as expired. Default 60s.
 */
export function isJwtExpired(token: string | null, skewSeconds = 60): boolean {
  if (token === null) return true;
  try {
    const { exp } = jwtDecode<DecodedJwt>(token);
    if (typeof exp !== 'number') return true;
    return exp <= Math.floor(Date.now() / 1000) + skewSeconds;
  } catch {
    // Malformed token -- treat as expired so callers fail closed.
    return true;
  }
}
